import { Component, Prop, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";

// Enums
import { ImageObjectTypes } from "@/models/images";
import { DataModules } from "@/store/modules/data";

// Types
import { IImage } from "@/store/types";
import { IState } from "@/store";
import { IClient } from "@/models/clients";
import { ConfirmModalModes } from "@/data/enums";

@Component({
  computed: {
    ...mapState({
      imageScope(state: IState, getters) {
        return getters[`data/${this.dataModule}/scope`](
          this.typeId,
          this.imageType
        );
      },
      image(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: this.dataModule,
          scope: this.imageScope
        });
      },
      imageSrc(state: IState, getters) {
        return getters[`data/${this.dataModule}/src`]({}, this.imageScope);
      },
      imageHash(state: IState, getters) {
        return getters[`data/${this.dataModule}/hash`](
          this.typeId,
          this.imageType
        );
      }
    })
  }
})
export class ImageProviderStore extends Mixins(RequestPromises) {
  // Getters
  public imageScope!: string;
  public image!: IImage;
  public imageSrc!: string;
  public imageHash!: string;

  // Props
  @Prop({ required: true })
  public typeId!: string;

  @Prop({ required: true })
  public imageType!: string;

  @Prop({ default: true })
  public isDefault!: boolean;

  @Prop({ default: true })
  public hasImage!: boolean;

  @Prop({ default: () => null })
  public clientId!: IClient["id"];

  // Variables
  public dataModule: string = DataModules.IMAGES;
  public loaders: Record<string, boolean> = {};
  public onUploadSuccess: () => any = () => Promise.resolve();
  public onDeleteSuccess: () => any = () => Promise.resolve();

  public created() {
    if (!this.hasImage) return;
    this.loadImage();
  }

  public async loadImage() {
    this.$set(this.loaders, "imageLoading", true);
    try {
      await this.getImage();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
    this.$delete(this.loaders, "imageLoading");
  }

  public async onFileChanged(image: any) {
    if (this.canUploadImage) {
      const data = new FormData();
      data.append("image", image);
      this.$set(this.loaders, "imageUploading", true);
      try {
        await this.uploadImage(data);
        if (this.imageType === ImageObjectTypes.BRAND) {
          await this.refreshBrandData();
        }
        await this.onUploadSuccess();
        this.$store.dispatch("toast/show", {
          message: this.$t("_.image_successfully_uploaded")
        });
      } catch (error) {
        this.$store.dispatch("api/handleValidationError", {
          error,
          vm: this
        });
      }
      this.$delete(this.loaders, "imageUploading");
    }
  }

  public async onDelete() {
    if (!this.canDeleteImage) {
      return;
    }
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          mode: ConfirmModalModes.BUTTON,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.delete_confirmation", {
            object: (this.$t("_.image") as string).toLowerCase()
          })
        },
        events: {
          confirmed: async () => {
            this.$set(this.loaders, "imageDeleting", true);
            try {
              await this.deleteImage();
              await this.onDeleteSuccess();
              if (this.imageType === ImageObjectTypes.BRAND) {
                await this.refreshBrandData();
              }
              this.$buefy.toast.open({
                message: this.$t("_.object_successfully_removed", {
                  object: this.$t("_.image")
                }) as string
              });
            } catch (error) {
              this.$store.dispatch("api/handleValidationError", {
                error,
                vm: this
              });
            }
            this.$delete(this.loaders, "imageDeleting");
            confirm.close();
          }
        }
      }
    });
  }

  public getImage() {
    return this.$store.dispatch(`data/${this.dataModule}/getImage`, {
      typeId: this.typeId,
      imageType: this.imageType,
      isDefault: this.isDefault,
      scope: this.imageScope,
      vm: this
    });
  }

  public uploadImage(data, guestToken = "") {
    if (this.imageType === ImageObjectTypes.CLIENT_CUSTOM_FIELD) {
      return this.$store.dispatch(
        `data/${this.dataModule}/uploadCustomFieldImage`,
        this.$_.merge(
          {
            fieldId: this.typeId,
            data,
            scope: this.imageScope,
            vm: this
          },
          guestToken ? { guestToken } : {}
        )
      );
    }

    return this.$store.dispatch(`data/${this.dataModule}/uploadImage`, {
      typeId: this.typeId,
      imageType: this.imageType,
      isDefault: this.isDefault,
      data,
      scope: this.imageScope,
      vm: this
    });
  }

  public deleteImage() {
    return this.$store.dispatch(`data/${this.dataModule}/deleteImage`, {
      typeId: this.typeId,
      imageType: this.imageType,
      hash: this.imageHash,
      scope: this.imageScope,
      vm: this
    });
  }

  public refreshBrandData() {
    return this.$store.dispatch("brand/get", { ignoreStored: true });
  }

  public get isComponentLoading() {
    return !this.$_.isEmpty(this.loaders);
  }

  public get canDeleteImage() {
    switch (this.imageType) {
      case ImageObjectTypes.CLIENT:
        return this.$userCan("delete_client_image");
      case ImageObjectTypes.USER:
        return this.$userCan("delete_user_image");
      case ImageObjectTypes.PRODUCT:
        return this.$userCan("delete_product_image");
      case ImageObjectTypes.BRAND:
        return this.$userCan("delete_brand_image");
      case ImageObjectTypes.BRAND_FAVICON:
        return this.$userCan("delete_brand_favicon");
      case ImageObjectTypes.BRAND_EMAIL_LOGO:
        return this.$userCan("delete_brand_email_logo");
    }
  }

  public get canUploadImage() {
    switch (this.imageType) {
      case ImageObjectTypes.CLIENT:
        return this.isDefault
          ? this.$userCan("upload_client_default_image")
          : this.$userCan("upload_client_image");
      case ImageObjectTypes.USER:
        return this.isDefault
          ? this.$userCan("upload_user_default_image")
          : this.$userCan("upload_user_image");
      case ImageObjectTypes.PRODUCT:
        return this.isDefault
          ? this.$userCan("upload_product_default_image")
          : this.$userCan("upload_product_image");
      case ImageObjectTypes.BRAND:
        return this.isDefault
          ? this.$userCan("upload_brand_default_image")
          : this.$userCan("upload_brand_image");
      case ImageObjectTypes.BRAND_FAVICON:
        return this.$userCan("upload_brand_favicon");
      case ImageObjectTypes.BRAND_EMAIL_LOGO:
        return this.$userCan("upload_brand_email_logo");
      case ImageObjectTypes.CLIENT_CUSTOM_FIELD:
        return true;
    }
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binSingle", {
      storeModule: this.dataModule,
      scope: this.imageScope,
      vm: this
    });
  }
}
